import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import defaultRecipe from '../assets/images/default-recipe.jpg';

// Carte de recette — univers Tartina (gourmand, chaleureux)
const RecipeCard = ({ recipe, showButton = true }) => {
  const {
    id,
    name,
    description,
    category,
    prep_time,
    rating,
    votes,
  } = recipe;

  const image = recipe.image || recipe.image_url || defaultRecipe;
  const cost = recipe.estimated_cost ?? recipe.price;
  const formattedCost =
    typeof cost === 'number' ? `${cost.toLocaleString('fr-FR')} FCFA` : cost;
  const formattedRating =
    typeof rating === 'number' ? rating.toFixed(1) : rating;

  return (
    <Card>
      <Media>
        <Img
          src={image}
          alt={name}
          loading="lazy"
          onError={(e) => {
            e.target.src = defaultRecipe;
          }}
        />
        {category && <CategoryTag>{category}</CategoryTag>}
        {prep_time && (
          <TimeTag>
            <i className="ti ti-clock" aria-hidden="true" /> {prep_time} min
          </TimeTag>
        )}
      </Media>

      <Content>
        <Title>{name}</Title>
        <Description>{description}</Description>

        <Footer>
          <Price>{formattedCost}</Price>
          <Rating title={votes ? `${votes.toLocaleString('fr-FR')} votes` : undefined}>
            <Star aria-hidden="true">★</Star>
            <span>{formattedRating}</span>
          </Rating>
        </Footer>

        {showButton && (
          <ActionButton to={`/recipes/${id}`}>
            Voir la recette
            <i className="ti ti-arrow-right" aria-hidden="true" />
          </ActionButton>
        )}
      </Content>
    </Card>
  );
};

const Card = styled.article`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: transform ${({ theme }) => theme.transitions.medium},
    box-shadow ${({ theme }) => theme.transitions.medium};

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const Media = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform ${({ theme }) => theme.transitions.slow};

  ${Card}:hover & {
    transform: scale(1.06);
  }
`;

const Tag = styled.span`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-size: 0.74rem;
  font-weight: 700;
`;

const CategoryTag = styled(Tag)`
  top: 12px;
  left: 12px;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.chocolate};
  text-transform: capitalize;
`;

const TimeTag = styled(Tag)`
  top: 12px;
  right: 12px;
  background: rgba(43, 33, 24, 0.78);
  color: #fff;
`;

const Content = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chocolate};
  margin-bottom: 8px;
`;

const Description = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.92rem;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Price = styled.span`
  background: ${({ theme }) => theme.colors.cream};
  color: ${({ theme }) => theme.colors.tertiary};
  font-weight: 800;
  font-size: 0.9rem;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
`;

const Rating = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.95rem;
`;

const Star = styled.span`
  color: ${({ theme }) => theme.colors.star};
  font-size: 1.05rem;
  line-height: 1;
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  text-decoration: none;
  text-align: center;
  padding: 11px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  font-weight: 700;
  font-size: 0.92rem;
  transition: background ${({ theme }) => theme.transitions.fast},
    transform ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
  i { font-size: 1rem; }
`;

export default RecipeCard;
